import { useEffect, useLayoutEffect } from "react";
/**
 * ── The one rule behind every layout effect in this package (T-414) ─────────
 *
 * **An overlay's behaviour must be live in the same commit the overlay becomes
 * observable.** A `useEffect` does not give that: React schedules passive
 * effects on the scheduler's own task, so between "the dialog is in the DOM"
 * and "the dialog's `Esc` listener exists" there is a window whose width is
 * decided by how busy the machine is. `useLayoutEffect` runs synchronously
 * inside the commit, before anything — a paint, a `MutationObserver`, a test's
 * next line — can observe the DOM the effect belongs to.
 *
 * That window is not theoretical. It is the whole **Esc-dismiss flake family**
 * (four members across `apps/web` and `apps/backoffice`, retro 2026-08-15):
 * `await findBy…("dialog")` resolves off a mutation record, the test fires
 * `Escape` at `document`, and on a loaded runner the listener that would have
 * answered is still queued behind other work. The test was right; the component
 * was late. A bigger timeout cannot fix it, because nothing is slow — the
 * dispatch and the listener are simply not ordered.
 *
 * **This module is deliberately not exported from `index.ts`.** It is a timing
 * decision internal to the shared shell, not a component contract.
 *
 * SSR-guarded: `useLayoutEffect` warns when there is no DOM to lay out. There
 * is no SSR in this app today; the guard costs nothing and keeps the package
 * honest if a fixture ever renders it to a string.
 */
export const useIsomorphicLayoutEffect = typeof document === "undefined" ? useEffect : useLayoutEffect;
//# sourceMappingURL=internal.js.map